/** Auto-calculation helpers for the Manual ZBC form.
 *
 * All numeric coefficients (density, offal, thickness thresholds, fillet
 * multiplier, etc.) are exposed via the Formula Registry so Corporate Admins
 * can view and edit them at runtime. Every helper accepts an optional `cfg`
 * object; if a key is present it overrides the built-in default. The default
 * export `DEFAULT_AUTOCALC_CFG` documents every knob.
 *
 * Every function returns either a plain value OR an object of the shape:
 *   { value, reason }        // value = the auto-suggested figure
 *                            // reason = human-readable line for the tooltip
 *
 * The form uses `reason` to display a small Sparkles-icon tooltip so the user
 * can see exactly how CostIQ arrived at a suggestion. All suggestions remain
 * fully editable — the moment a user types their own value, the auto-suggestion
 * is treated as overridden and no longer re-fires.
 */

const num = (v) => parseFloat(v) || 0;
const round3 = (v) => Math.round(v * 1000) / 1000;

/** Default coefficients — mirror the seeded Formula Registry entries. */
export const DEFAULT_AUTOCALC_CFG = {
    OFFAL_MM: 5.0,                     // formula: autocalc_offal_mm
    STEEL_DENSITY_G_PER_CC: 7.85,      // formula: autocalc_density_steel
    AL_DENSITY_G_PER_CC: 2.70,         // formula: autocalc_density_aluminium
    BRASS_DENSITY_G_PER_CC: 8.50,      // formula: autocalc_density_brass
    LASER_MAX_MM: 10.0,                // formula: autocalc_cutting_threshold_laser
    PLASMA_MAX_MM: 16.0,               // formula: autocalc_cutting_threshold_plasma
    FILLET_MULT: 0.7,                  // formula: autocalc_fillet_multiplier
    FILLET_MIN: 3.0,                   // formula: autocalc_fillet_min_mm
    FILLET_MAX: 6.0,                   // formula: autocalc_fillet_max_mm
    CAPEX_DEFAULT_YEARS: 3,            // formula: capex_default_amort_years
    // Sprint 4 · Option C — Finish Weight auto-derive loss %s (Registry-editable)
    round_bar_machining_loss_pct: 45.0,
    prismatic_machining_loss_pct: 40.0,
    pipe_machining_loss_pct: 10.0,
};

/** Map from Formula Registry id → key inside cfg. */
export const AUTOCALC_FORMULA_MAP = {
    autocalc_offal_mm: "OFFAL_MM",
    autocalc_density_steel: "STEEL_DENSITY_G_PER_CC",
    autocalc_density_aluminium: "AL_DENSITY_G_PER_CC",
    autocalc_density_brass: "BRASS_DENSITY_G_PER_CC",
    autocalc_cutting_threshold_laser: "LASER_MAX_MM",
    autocalc_cutting_threshold_plasma: "PLASMA_MAX_MM",
    autocalc_fillet_multiplier: "FILLET_MULT",
    autocalc_fillet_min_mm: "FILLET_MIN",
    autocalc_fillet_max_mm: "FILLET_MAX",
    capex_default_amort_years: "CAPEX_DEFAULT_YEARS",
    round_bar_machining_loss_pct: "round_bar_machining_loss_pct",
    prismatic_machining_loss_pct: "prismatic_machining_loss_pct",
    pipe_machining_loss_pct: "pipe_machining_loss_pct",
};

/** Merge Formula Registry overrides (as returned by GET /formulas) onto defaults. */
export function buildAutoCalcConfig(overrides = {}) {
    const cfg = { ...DEFAULT_AUTOCALC_CFG };
    for (const [fid, key] of Object.entries(AUTOCALC_FORMULA_MAP)) {
        const v = overrides?.[fid];
        if (v !== undefined && v !== null && !Number.isNaN(parseFloat(v))) {
            cfg[key] = parseFloat(v);
        }
    }
    return cfg;
}

const cfgOr = (cfg, key) => (cfg && cfg[key] != null ? cfg[key] : DEFAULT_AUTOCALC_CFG[key]);

/* ------------------------------------------------------------------ */
/* Grade-driven derivations                                            */
/* ------------------------------------------------------------------ */

/**
 * Return density in g/cc for a given steel-grade code. Defaults to steel for
 * anything that isn't an aluminium / brass / copper grade (future-proofing).
 */
export function deriveDensity(gradeCode = "", cfg = DEFAULT_AUTOCALC_CFG) {
    const code = (gradeCode || "").toUpperCase();
    const steel = cfgOr(cfg, "STEEL_DENSITY_G_PER_CC");
    const al = cfgOr(cfg, "AL_DENSITY_G_PER_CC");
    const brass = cfgOr(cfg, "BRASS_DENSITY_G_PER_CC");
    if (code.includes("AL") && !code.startsWith("SAILMA")) {
        return { value: al, reason: `Aluminium alloy — density ${al} g/cc` };
    }
    if (code.startsWith("BRASS") || code.startsWith("CU")) {
        return { value: brass, reason: `Brass / copper — density ${brass} g/cc` };
    }
    return { value: steel, reason: `Carbon / structural steel — density ${steel} g/cc` };
}

/**
 * Auto-suggest RM Category based on the grade code / spec text. Sheet-metal
 * grades → SHEET_STEEL, tube grades → SQUARE_TUBE, bar grades → ROUND_BAR.
 */
export function deriveRmCategoryFromGrade(grade) {
    if (!grade) return null;
    const code = (grade.code || "").toUpperCase();
    const spec = (grade.spec || grade.name || "").toUpperCase();

    if (code.startsWith("IS4923") || code.startsWith("IS1239") ||
        spec.includes("HOLLOW SECTION") || spec.includes("TUBE")) {
        return { value: "SQUARE_TUBE", reason: `Grade ${grade.code} spec matches "tube / hollow section"` };
    }
    if (code.includes("SAE") && (code.includes("10") || code.includes("11")) ||
        spec.includes("ROUND BAR") || spec.includes("BAR STOCK")) {
        return { value: "ROUND_BAR", reason: `Grade ${grade.code} typically supplied as round bar` };
    }
    // Cold-rolled, hot-rolled flat, HSLA plate → sheet steel
    if (code.startsWith("IS513") || code.startsWith("IS1079") || code.startsWith("IS2062") ||
        code.startsWith("IS5986") || code.startsWith("HARDOX") || code.startsWith("DOMEX") ||
        code.startsWith("SAILMA") || code.startsWith("S") || code.startsWith("DD11") ||
        code.startsWith("ASTM-A") || code.startsWith("EN-S") || code.startsWith("JIS-SS") ||
        spec.includes("HOT ROLLED") || spec.includes("COLD ROLLED") || spec.includes("PLATE")) {
        return { value: "SHEET_STEEL", reason: `Grade ${grade.code} is a flat-rolled / plate steel` };
    }
    return null;
}

/**
 * Auto-suggest a scrap type based on the steel grade. Uses the seeded scrap
 * codes: MS-HEAVY, CR-SCRAP, HARDOX-SCRAP, TURNINGS, MS-LIGHT.
 */
export function deriveScrapCodeFromGrade(grade) {
    if (!grade) return null;
    const code = (grade.code || "").toUpperCase();
    if (code.startsWith("HARDOX") || code.startsWith("DOMEX")) {
        return { value: "HARDOX-SCRAP", reason: `${grade.code} is abrasion-resistant — sold as Hardox / Domex scrap` };
    }
    if (code.startsWith("IS513") || code.includes("CR")) {
        return { value: "CR-SCRAP", reason: `${grade.code} is cold-rolled — offcuts sold as CR scrap` };
    }
    if (code.includes("SAE") || code.startsWith("EN-")) {
        return { value: "TURNINGS", reason: `${grade.code} bar / machined stock — offcuts are turnings` };
    }
    return { value: "MS-HEAVY", reason: `${grade.code} is structural steel — offcuts sold as MS Heavy scrap` };
}

/* ------------------------------------------------------------------ */
/* Weight calculations                                                 */
/* ------------------------------------------------------------------ */

/**
 * Sheet blank input weight with an offal allowance for cutting.
 * offalSideMm defaults to cfg.OFFAL_MM (5 mm) — matches AI-ZBC raw material engine.
 */
export function sheetInputWeightWithOffal(w, l, t, density = 7.85, offalSideMm = null, cfg = DEFAULT_AUTOCALC_CFG) {
    const wMm = num(w), lMm = num(l), tMm = num(t);
    if (!wMm || !lMm || !tMm) return null;
    const offal = offalSideMm != null ? offalSideMm : cfgOr(cfg, "OFFAL_MM");
    const gW = wMm + 2 * offal;
    const gL = lMm + 2 * offal;
    const weight = round3((gW * gL * tMm * density) / 1_000_000);
    return {
        value: weight,
        reason: `(${wMm}+${2 * offal}) × (${lMm}+${2 * offal}) × ${tMm} × ${density} / 1,000,000\n= gross blank ${gW}×${gL}×${tMm} mm × density = ${weight} kg`,
        gross_blank_width: gW,
        gross_blank_length: gL,
    };
}

/**
 * Round bar input weight — π × OD² × length × density / 4,000,000
 */
export function roundBarInputWeightDetail(od, length, density = 7.85) {
    const dMm = num(od), lMm = num(length);
    if (!dMm || !lMm) return null;
    const weight = round3((Math.PI * dMm * dMm * lMm * density) / 4_000_000);
    return {
        value: weight,
        reason: `π × ${dMm}² × ${lMm} × ${density} / 4,000,000 = ${weight} kg`,
    };
}

/**
 * Square hollow section (HSS / RHS) input weight.
 * Cross-section area = a² − (a − 2t)²  for square tube of side `a` and wall `t`.
 * Accepts `tubeSize` string like "40×40×2" OR "40x40x2" — parses side + wall.
 */
export function squareTubeInputWeight(tubeSize, tubeLengthMm, thicknessMm, density = 7.85) {
    let side = 0, wall = num(thicknessMm);
    const s = (tubeSize || "").trim().toLowerCase().replace(/×/g, "x");
    const parts = s.split(/x/).map((p) => parseFloat(p)).filter((v) => !isNaN(v));
    if (parts.length >= 1) side = parts[0];
    if (parts.length >= 3 && !wall) wall = parts[2];
    const len = num(tubeLengthMm);
    if (!side || !wall || !len) return null;
    const inner = side - 2 * wall;
    if (inner <= 0) return null;
    const area = side * side - inner * inner;   // mm²
    const weight = round3((area * len * density) / 1_000_000);
    return {
        value: weight,
        reason: `HSS ${side}×${side}×${wall} × ${len} mm × ${density}\n= (${side}² − ${inner.toFixed(1)}²) × ${len} × ${density} / 1e6\n= ${weight} kg`,
    };
}

/**
 * Detect if a ZBC represents a machined part — mirrors the backend guard in
 * `zbc_engine._is_machined_part`. Used by ZBCForm to skip Finish-Weight
 * auto-derivation and show a red "Enter Finish Weight from drawing" banner.
 */
const MACHINING_OP_KEYWORDS = [
    "TURN", "FACE", "BORE", "BORING", "DRILL", "TAP", "MILL",
    "CHAMFER", "CHAMPER", "PART", "REAM", "GROOV", "COUNTER",
];
export function isMachinedPart(rmCategory, operations = []) {
    if ((rmCategory || "").toUpperCase() === "ROUND_BAR") return true;
    for (const op of operations || []) {
        const name = `${op?.name || ""} ${op?.operation || ""} ${op?.op_name || ""}`.toUpperCase();
        if (MACHINING_OP_KEYWORDS.some((kw) => name.includes(kw))) return true;
    }
    return false;
}

/**
 * Auto-derive Finish Weight from Input Weight.
 *
 * Sprint 4 · Option C — always returns a suggested value; the caller may pass
 * `rmCategory` + `operations` so machined parts use a shape-specific loss %
 * from the Formula Registry instead of the sheet-metal 5.5% default.
 *
 * Returns { value, source, confidence, lossPct } where:
 *   source     ∈ "SHEET_LOSS_RULE" | "SHAPE_RULE" (round bar / prismatic / pipe)
 *   confidence ∈ "MEDIUM" (shape rule) | "LOW" (sheet fallback)
 *   lossPct    = the % applied (for the tooltip)
 * AI-extracted drawing weights (source=HIGH) are set by the AI Wizard directly
 * — never by this helper.
 */
export function deriveFinishWeight(inputWeightKg, scrapAllowances = {}, opts = {}) {
    const iw = num(inputWeightKg);
    if (!iw) return null;
    const rm = (opts.rmCategory || "").toUpperCase();
    const machined = opts.isMachinedPart || isMachinedPart(rm, opts.operations || []);
    // Shape-specific loss %s (Registry-editable). Defaults track backend constants.
    const roundBarLoss  = num(opts.roundBarMachiningLossPct)  || 45.0;
    const prismaticLoss = num(opts.prismaticMachiningLossPct) || 40.0;
    const pipeLoss      = num(opts.pipeMachiningLossPct)      || 10.0;

    if (rm === "ROUND_BAR" || (machined && !["SHEET_STEEL", "SHEET_METAL_FORMED"].includes(rm))) {
        return {
            value: Number((iw * (1 - roundBarLoss / 100)).toFixed(3)),
            source: "SHAPE_RULE",
            confidence: "MEDIUM",
            lossPct: roundBarLoss,
            shape: "ROUND_BAR",
        };
    }
    if (["SQUARE_BAR", "HEX_BAR"].includes(rm)) {
        return {
            value: Number((iw * (1 - prismaticLoss / 100)).toFixed(3)),
            source: "SHAPE_RULE",
            confidence: "MEDIUM",
            lossPct: prismaticLoss,
            shape: rm,
        };
    }
    if (["PIPE", "TUBE"].includes(rm)) {
        return {
            value: Number((iw * (1 - pipeLoss / 100)).toFixed(3)),
            source: "SHAPE_RULE",
            confidence: "MEDIUM",
            lossPct: pipeLoss,
            shape: rm,
        };
    }
    // Sheet-metal / plate — original blanking-loss recipe (recoverable + irrecoverable + burning)
    const rec = num(scrapAllowances.recoverable_pct) || 4.0;
    const irr = num(scrapAllowances.irrecoverable_pct) || 1.0;
    const burn = num(scrapAllowances.burning_loss_pct) || 0.5;
    const totalLoss = rec + irr + burn;
    const fw = round3(iw * (1 - totalLoss / 100));
    return {
        value: fw,
        source: "SHEET_LOSS_RULE",
        confidence: "LOW",
        lossPct: totalLoss,
        shape: "SHEET",
        reason: `Input ${iw} kg × (1 − ${totalLoss}% total loss)\n  · ${rec}% recoverable + ${irr}% irrecoverable + ${burn}% burning loss\n= ${fw} kg`,
    };
}

/**
 * Read the recoverable / irrecoverable / burning-loss % from the seeded
 * SCRAP_RATES master (rows with unit === "PCT").
 */
export function extractScrapAllowances(scraps = [], cuttingProcess = "LASER_CUTTING") {
    const pctRow = (code) => scraps.find((s) => s.code === code)?.rate_per_kg;
    return {
        recoverable_pct: pctRow("RECOVERABLE_PCT"),
        irrecoverable_pct: pctRow("IRRECOVERABLE_PCT"),
        burning_loss_pct: cuttingProcess === "PLASMA_CUTTING"
            ? pctRow("BURNING_LOSS_PLASMA")
            : pctRow("BURNING_LOSS_LASER"),
    };
}

/* ------------------------------------------------------------------ */
/* Section 4 — Cutting suggestions                                     */
/* ------------------------------------------------------------------ */

/** Rectangular blank perimeter in metres. Ignored for tubes / bars. */
export function deriveCutPerimeter(widthMm, lengthMm) {
    const w = num(widthMm), l = num(lengthMm);
    if (!w || !l) return null;
    const p = round3((2 * (w + l)) / 1000);
    return {
        value: p,
        reason: `2 × (${w} + ${l}) / 1000 = ${p} Mtr (rectangular blank; adjust for cut-outs / slots)`,
    };
}

/** Auto-suggest cutting process based on plate thickness. */
export function deriveCuttingProcess(thicknessMm, cfg = DEFAULT_AUTOCALC_CFG) {
    const t = num(thicknessMm);
    if (!t) return null;
    const laserMax = cfgOr(cfg, "LASER_MAX_MM");
    const plasmaMax = cfgOr(cfg, "PLASMA_MAX_MM");
    if (t <= laserMax) return { value: "LASER_CUTTING", reason: `${t} mm ≤ ${laserMax} mm — fibre laser is most economical` };
    if (t <= plasmaMax) return { value: "PLASMA_CUTTING", reason: `${t} mm ≤ ${plasmaMax} mm — plasma is the standard bracket` };
    return { value: "GAS_CUTTING", reason: `${t} mm plate — oxy-fuel / gas cutting used above ${plasmaMax} mm` };
}

/* ------------------------------------------------------------------ */
/* Section 5 — Welding suggestions                                     */
/* ------------------------------------------------------------------ */

/**
 * Structural rule-of-thumb: fillet leg ≈ min(FILLET_MULT × thickness, FILLET_MAX)
 * clamped to at least FILLET_MIN. IS 800 / AWS D1.1 minimum-fillet guidance.
 */
export function deriveFilletSize(thicknessMm, cfg = DEFAULT_AUTOCALC_CFG) {
    const t = num(thicknessMm);
    if (!t) return null;
    const mult = cfgOr(cfg, "FILLET_MULT");
    const flMin = cfgOr(cfg, "FILLET_MIN");
    const flMax = cfgOr(cfg, "FILLET_MAX");
    const raw = Math.min(mult * t, flMax);
    const rounded = Math.max(flMin, Math.round(raw));
    return {
        value: rounded,
        reason: `Rule-of-thumb: fillet ≈ min(${mult} × ${t}, ${flMax} mm) → ${raw.toFixed(1)} mm → clamped to ≥ ${flMin}, rounded to ${rounded} mm`,
    };
}

/* ------------------------------------------------------------------ */
/* Section 3 — Conversion / operation suggestions                      */
/* ------------------------------------------------------------------ */

/** Machine type ↔ keywords in the operation name. */
const OP_KEYWORD_MAP = [
    { types: ["LASER"], keywords: ["laser", "fiber cut"] },
    { types: ["CNC_PRESS_BRAKE"], keywords: ["bend", "brake", "form"] },
    { types: ["ROBOT_WELDER", "MIG_WELDER"], keywords: ["weld", "mig", "tig", "argon"] },
    { types: ["DRILL"], keywords: ["drill", "tap"] },
    { types: ["BANDSAW"], keywords: ["saw", "cut off", "cutoff"] },
    { types: ["MECH_PRESS", "HYD_PRESS"], keywords: ["press", "punch", "blank", "coin"] },
    { types: ["PAINT_LINE"], keywords: ["paint", "ced", "powder coat", "coating"] },
];

/**
 * Given a free-text operation name, pick the best-fitting machine from the
 * plant's active machines. Returns null if no keyword match.
 */
export function deriveMachineFromOpName(opName, plantMachines = []) {
    const name = (opName || "").toLowerCase();
    if (!name) return null;
    for (const rule of OP_KEYWORD_MAP) {
        if (rule.keywords.some((k) => name.includes(k))) {
            // Pick the first ACTIVE machine of a matching type
            for (const t of rule.types) {
                const m = plantMachines.find((mch) => mch.type === t);
                if (m) {
                    return {
                        value: m.id,
                        machine_code: m.code,
                        reason: `"${opName}" matched keyword → machine type ${t} → picked ${m.code}`,
                    };
                }
            }
        }
    }
    return null;
}
